"use client";

import type React from "react";
import { useState, useEffect, useCallback } from "react";
import {
  Box,
  FormControl,
  FormLabel,
  Input,
  Text,
  Link,
  VStack,
  useToast,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from "@chakra-ui/react";
import { ExternalLinkIcon } from "@chakra-ui/icons";
import { agentService } from "../../services/agentService.ts";
import { useAppTheme } from "../../ThemeContext.tsx";

interface WebhookSettingsProps {
  agentId: string;
  initialWebhookUrl?: string | null;
}

export const WebhookSettings: React.FC<WebhookSettingsProps> = ({
  agentId,
  initialWebhookUrl,
}) => {
  const [webhookUrl, setWebhookUrl] = useState<string>(initialWebhookUrl || "");
  const [isUpdating, setIsUpdating] = useState(false);
  const toast = useToast();
  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";

  // Set initial webhook URL when it becomes available
  useEffect(() => {
    if (initialWebhookUrl !== undefined) {
      setWebhookUrl(initialWebhookUrl || "");
    }
  }, [initialWebhookUrl]);

  const handleWebhookUrlUpdate = useCallback(async () => {
    if (isUpdating) return;

    setIsUpdating(true);
    try {
      await agentService.update(agentId, {
        webhook_url: webhookUrl || null,
      });

      toast({
        title: "Webhook URL updated",
        status: "success",
        duration: 2000,
      });
    } catch (error: any) {
      console.error("Error updating webhook URL:", error);
      toast({
        title: "Error updating webhook URL",
        description:
          error.response?.data?.message || "An unexpected error occurred",
        status: "error",
        duration: 3000,
      });

      // Reset to initial value on error
      if (initialWebhookUrl !== undefined) {
        setWebhookUrl(initialWebhookUrl || "");
      }
    } finally {
      setIsUpdating(false);
    }
  }, [agentId, webhookUrl, initialWebhookUrl, isUpdating, toast]);

  return (
    <Accordion allowToggle>
      <AccordionItem border="none">
        <h2>
          <AccordionButton
            py={3}
            px={4}
            _hover={{ bg: isDark ? "gray.800" : "gray.200" }}
          >
            <Box
              flex="1"
              textAlign="left"
              fontSize="sm"
              display="flex"
              alignItems="center"
              gap={2}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M10 3.125L15.9375 6.5625V13.4375L10 16.875L4.0625 13.4375V6.5625L10 3.125ZM5.93367 6.92356L10.0001 9.27775L14.0664 6.92359L10 4.56938L5.93367 6.92356ZM5.3125 8.00831V12.7168L9.37506 15.0688V10.3603L5.3125 8.00831ZM10.6251 15.0688L14.6875 12.7168V8.00836L10.6251 10.3603V15.0688Z"
                    fill="currentColor"
                  ></path>
                </svg>
              </svg>
              Webhook Settings
            </Box>

            <AccordionIcon />
          </AccordionButton>
        </h2>
        <AccordionPanel pb={4} bg={isDark ? "gray.800" : "gray.100"}>
          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel
                fontSize="sm"
                fontWeight="medium"
                color={isDark ? "gray.300" : "gray.700"}
              >
                Agent Level Webhook URL
              </FormLabel>
              <Text
                fontSize="xs"
                color={isDark ? "gray.400" : "gray.600"}
                mb={2}
              >
                Webhook URL to receive events.
                {/* {" "} */}
                {/* <Link
                  href="https://docs.retellai.com/features/webhook-overview"
                  color={isDark ? "gray.300" : "gray.600"}
                  isExternal
                >
                  (Learn more) <ExternalLinkIcon mx="2px" />
                </Link> */}
              </Text>
              <Input
                id="webhook-url"
                value={webhookUrl}
                onChange={(e) => setWebhookUrl(e.target.value)}
                onBlur={handleWebhookUrlUpdate}
                placeholder="https://your-webhook-url.com/events"
                size="sm"
                bg={isDark ? "gray.700" : "white"}
                borderColor={isDark ? "gray.600" : "gray.300"}
                _hover={{ borderColor: isDark ? "gray.500" : "gray.400" }}
                _focus={{
                  borderColor: isDark ? "gray.500" : "gray.400",
                  boxShadow: "none",
                }}
              />
            </FormControl>
          </VStack>
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
};
